import { FC, ReactNode, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import {
  IInfoBlockCommon,
  InfoBlock,
} from '../../../../shared/components/infoBlock/infoBlock.components.tsx';
import { useTranslation } from 'react-i18next';
/* Icons */
import PhoneIcon from '@mui/icons-material/Phone';
import MobileIcon from '@mui/icons-material/PhoneIphone';
import FaxIcon from '@mui/icons-material/Fax';
import Link from '@mui/material/Link';
import EmailIcon from '@mui/icons-material/AlternateEmail';
import { List, ListItem, ListItemText, useMediaQuery } from '@mui/material';
import { styled } from '@mui/material/styles';
import { ICustomerInfo } from '../../create/states/orderDetails/orderDetails.state.ts';

export const CustomerInfo: FC<ICustomerInfoExpanded> = ({
  internalOrFullName = '',
  customerNo = '',
  emailTip,
  isNotePinned,
  isAddressPinned,
  informationTip,
  contacts = {},
  ...rest
}) => {
  const { t } = useTranslation();
  const formContext = useFormContext();
  const isFullHd = useMediaQuery('(min-width:1920px)');
  const {
    deliveryAddress: _deliveryAddress,
    discountKindId: _discountKindId,
    isDeliverySplitting: _isDeliverySplitting,
    deliverySplittingPartsCount: _deliverySplittingPartsCount,
    transportSector: _transportSector,
    transportSectorId: _transportSectorId,
    id,
    addressValue,
    ...props
  } = rest;
  const getIcon = (field: string): ReactNode => {
    switch (field) {
      case 'phoneMobile':
        return <MobileIcon />;
      case 'fax':
        return <FaxIcon />;
      case 'email':
        return <EmailIcon />;
      default:
        return <PhoneIcon />;
    }
  };

  const getHref = (field: string, value: string) => {
    if (field === 'email') return `mailto:${value.trim()}`;

    const sanitized = value.replace(/[^+\d]/g, ''); // delete all non-digits or non-plus sign to make tel: valid
    return `tel:${sanitized}`;
  };

  const Contacts = useMemo(() => {
    const contactArr = Object.entries(contacts).reduce((acc, item, i) => {
      const field = item[0];
      const value = item[1];
      if (value && !(typeof value === 'object')) {
        acc.push(
          <SListItem
            title={t(`order.${field.replace(/[A-Z]/g, (match) => `_${match.toLowerCase()}`)}`)}
            key={i}
          >
            {getIcon(field)}
            <SContactValue
              disableTypography
              primary={<Link href={getHref(field, value)}>{value}</Link>}
            />
          </SListItem>,
        );
      }
      return acc;
    }, [] as ReactNode[]);
    const shouldUseColumnsOnStandardScreen =
      contactArr.length > 3 && !isNotePinned && !isAddressPinned && !isFullHd;
    const shouldUseColumnsOnFullHdScreen = isFullHd && contactArr.length > 3;
    const needColumns = shouldUseColumnsOnStandardScreen || shouldUseColumnsOnFullHdScreen;
    return (
      <SList
        sx={{
          columns: needColumns ? 2 : 'initial',
        }}
      >
        {contactArr}
      </SList>
    );
  }, [contacts, t, isNotePinned, isAddressPinned, isFullHd]);

  const register = addressValue === undefined ? formContext?.register : undefined;
  if (addressValue === undefined && !formContext) {
    throw new Error('CustomerInfo: "addressValue" is required when used outside a FormProvider');
  }

  return (
    <InfoBlock
      customerName={`${customerNo} ${internalOrFullName}`}
      rightColumn={Contacts}
      {...(register ? { register } : { addressValue })}
      infoTip={{ text: informationTip, readonly: true, customerId: id }}
      emailTip={emailTip}
      isNotePinned={isNotePinned}
      isAddressPinned={isAddressPinned}
      {...props}
    />
  );
};

const SList = styled(List)(({ theme }) => ({
  padding: 0,
  '& .MuiSvgIcon-root': {
    width: 20,
    height: 20,
    color: theme.palette.grey['600'],
  },
}));

const SListItem = styled(ListItem)(() => ({
  padding: 0,
  marginBottom: 3,
  minWidth: 120,
}));

const SContactValue = styled(ListItemText)(({ theme }) => ({
  marginLeft: 8,
  '& a': {
    fontSize: 14,
    fontWeight: 400,
    fontFamily: theme.typography.fontFamily,
    color: 'inherit',
    textDecoration: 'none',
  },
}));

interface ICustomerInfoExpanded extends ICustomerInfo {
  emailTip?: IInfoBlockCommon['emailTip'];
  isNotePinned?: boolean;
  isAddressPinned?: boolean;
}
